import {
    formatNotificationLocaleDateTime,
    getAgeInYearsFromYyyyMmDd,
} from "../helpers";
import "./NotificationDetailsSheet.scss";

/**
 * Grid rows: `{ key }` selects `details[key]` or a virtual formatter; `{ label }` is the visible title.
 * Order here is display order in the member section.
 *
 * @type {ReadonlyArray<{ key: string, label: string }>}
 */
const MEMBER_DETAIL_FIELDS = [
    { key: "memberFullName", label: "Member name" },
    { key: "recipientId", label: "Member ID" },
    { key: "pcpFullName", label: "PCP name" },
    { key: "pcpNetwork", label: "PCP network" },
    { key: "pcpNpi", label: "PCP NPI" },
    { key: "eligibilityType", label: "Eligibility type" },
    { key: "memberDob", label: "Member age" },
];

/**
 * Virtual keys for the grid: each maps to `[firstNameField, lastNameField]` on the details payload.
 *
 * @type {Readonly<Record<string, readonly [string, string]>>}
 */
const FULL_NAME_FIELDS = {
    memberFullName: ["memberFirstName", "memberLastName"],
    pcpFullName: ["pcpFirstName", "pcpLastName"],
};

/**
 * Concatenates first and last name if at least one part is non-empty (trimmed); otherwise `—`.
 *
 * @param {Record<string, unknown>} details Notification details from `fetchNotificationDetails`.
 * @param {string} firstKey Field key for the given name.
 * @param {string} lastKey Field key for the family name.
 * @returns {string}
 */
function formatJoinedFullName(details, firstKey, lastKey) {
    const parts = [details[firstKey], details[lastKey]].filter(
        (p) => p != null && String(p).trim() !== "",
    );
    return parts.length ? parts.join(" ") : "—";
}

/**
 * Produces the display string for one cell in the member details grid.
 *
 * - **`memberFullName` / `pcpFullName`:** resolved via `FULL_NAME_FIELDS` and `formatJoinedFullName`.
 * - **`memberDob`:** localized DOB (date only) plus `getAgeInYearsFromYyyyMmDd`-based age label.
 * - **Other keys:** `details[key]`; falsy → `—`.
 *
 * @param {string} key Field key from `MEMBER_DETAIL_FIELDS`.
 * @param {Record<string, unknown>} details Notification details payload.
 * @returns {string} Cell text (often `String(raw)` for simple fields).
 */
function formatMemberDetailValue(key, details) {
    const nameFields = FULL_NAME_FIELDS[key];
    if (nameFields) {
        const [firstKey, lastKey] = nameFields;
        return formatJoinedFullName(details, firstKey, lastKey);
    }

    if (key === "memberDob") {
        const dob = details.memberDob;
        const age = getAgeInYearsFromYyyyMmDd(dob);
        if (!dob || !age) {
            return "—";
        }

        return `${formatNotificationLocaleDateTime(String(dob), { includeTime: false })} (${age} years)`;
    }

    const raw = details[key];
    if (!raw) {
        return "—";
    }

    return raw;
}

/**
 * Member summary grid for loaded notification details (`fetchNotificationDetails` payload).
 *
 * @param {object} props
 * @param {Record<string, unknown>} props.details Loaded notification details.
 * @returns {import('react').JSX.Element}
 */
const MemberDetails = ({ details }) => {
    return (
        <section
            className="notification-details-sheet__section"
            aria-labelledby="notification-details-member-heading"
        >
            <h2
                id="notification-details-member-heading"
                className="notification-details-sheet__section-title"
            >
                Member details
            </h2>
            <div
                className="notification-details-sheet__grid"
                role="list"
            >
                {MEMBER_DETAIL_FIELDS.map(({ key, label }) => (
                    <div
                        key={key}
                        className="notification-details-sheet__grid-cell"
                        role="listitem"
                    >
                        <span className="notification-details-sheet__grid-label">
                            {label}
                        </span>
                        <span className="notification-details-sheet__grid-value">
                            {formatMemberDetailValue(key, details)}
                        </span>
                    </div>
                ))}
            </div>
        </section>
    );
};

export default MemberDetails;
